import { FC, useMemo, ReactNode } from 'react';
import { Box, Typography, SxProps } from '@mui/material';
import { InterestsEnum } from '@sweatpals/shared';
import { Showable, useStyles } from '@sweatpals/react-ui-kit';
import { map } from 'lodash-es';
import { browseActivitiesByInterestsStyles } from './browse-activities-by-interests.styles';
import ButtonsGroup from '@/modules/_core/components/inputs/button-group.component';
import { INTERESTS, LocationOptionsMap } from '@/modules/_core/constants';
import FitnessIcon from '@/modules/_core/components/images/Fitness-icon';

export interface BrowseActivitiesByInterestsProps {
	title?: string | ReactNode;
	showCitySelector?: boolean;
	selectedInterests?: InterestsEnum[];
	selectedLocations?: string[];
	excludeInterests?: string[];
	excludeLocations?: string[];
	onLocationChange?(city: string): void;
	onInterestsChange?(interest: string): void;
}

const BrowseActivitiesByInterests: FC<BrowseActivitiesByInterestsProps> = ({
	title,
	showCitySelector,
	selectedInterests = [],
	selectedLocations = [],
	excludeLocations = [],
	excludeInterests = [],
	onLocationChange,
	onInterestsChange,
}) => {
	const locations = useMemo(
		() =>
			Object.values(LocationOptionsMap)
				.map(city => ({ name: city.name, value: city.cityId }))
				.filter(city => !excludeLocations?.includes(city.value)),
		[excludeLocations]
	);

	const interests = useMemo(
		() => map(INTERESTS, interest => ({ name: interest.renderName, value: interest.id }))
			.filter(interest => !excludeInterests?.includes(interest.value)),
		[excludeInterests]
	);

	const renderItem = (item: { name: string; value: string }, sx?: SxProps) => (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
			<FitnessIcon fitnessTypeId={item.value} iconWidth={20} iconHeight={20} />
			<Typography sx={sx}>{item.name}</Typography>
		</Box>
	);

	const styles = useStyles(browseActivitiesByInterestsStyles);

	return (
		<Box sx={styles.root}>
			<Typography sx={styles.title}>{title || 'browse your favorite activities'}</Typography>

			<Box sx={styles.buttonSection}>
				<Showable show={showCitySelector}>
					<Typography sx={styles.subtitle}>Activities</Typography>
				</Showable>

				<ButtonsGroup
					items={interests}
					onChangeHandler={onInterestsChange}
					selectedItems={selectedInterests}
					renderItem={renderItem}
					sx={{
						rootSx: {
							justifyContent: 'flex-start',
							flexWrap: 'wrap',
						},
					}}
				/>
			</Box>

			<Showable show={showCitySelector}>
				<Box sx={styles.buttonSection}>
					<Typography sx={styles.subtitle}>City</Typography>
					<ButtonsGroup
						items={locations}
						onChangeHandler={onLocationChange}
						selectedItems={selectedLocations}
						sx={{
							rootSx: {
								justifyContent: 'flex-start',
								flexWrap: 'wrap',
							},
						}}
					/>
				</Box>
			</Showable>
		</Box>
	);
};

export default BrowseActivitiesByInterests;
